
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Lead } from "@/types/leads";

interface LeadsFilter {
  period: 'last7days' | 'last30days' | 'custom';
  startDate?: Date;
  endDate?: Date;
}

export const useLeads = () => {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filter, setFilter] = useState<LeadsFilter>({ 
    period: 'last7days'
  });
  
  const fetchLeads = async () => {
    setIsLoading(true);
    try {
      let query = supabase
        .from("leads")
        .select("id, name, email, phone, page, created_at")
        .order("created_at", { ascending: false });
      
      // Apply date filter
      if (filter.period === 'last7days') {
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        query = query.gte('created_at', sevenDaysAgo.toISOString());
      } else if (filter.period === 'last30days') {
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        query = query.gte('created_at', thirtyDaysAgo.toISOString());
      } else if (filter.period === 'custom' && filter.startDate && filter.endDate) {
        query = query
          .gte('created_at', filter.startDate.toISOString())
          .lte('created_at', filter.endDate.toISOString());
      }
      
      // Apply search filter on name, email or phone
      if (searchQuery) {
        query = query.or(`name.ilike.%${searchQuery}%,email.ilike.%${searchQuery}%,phone.ilike.%${searchQuery}%`);
      }
      
      const { data, error: queryError } = await query;
      
      if (queryError) {
        throw queryError;
      }
      
      setLeads(data as Lead[]);
      setError(null);
    } catch (err: any) {
      console.error("Error fetching leads:", err);
      setError(err.message || "Erro ao carregar leads");
    } finally {
      setIsLoading(false);
    }
  };
  
  useEffect(() => {
    fetchLeads();
  }, [searchQuery, filter]);
  
  const refresh = () => {
    fetchLeads();
  };

  const fetchLastLead = async (): Promise<Lead | null> => {
    try {
      const { data, error } = await supabase
        .from("leads")
        .select("id, name, email, phone, page, created_at")
        .order("created_at", { ascending: false })
        .limit(1)
        .single();
      
      if (error) {
        console.error("Error fetching last lead:", error);
        return null;
      }
      
      return data as Lead;
    } catch (err) {
      console.error("Error in fetchLastLead:", err);
      return null;
    }
  };
  
  return {
    leads,
    isLoading,
    error,
    searchQuery,
    setSearchQuery,
    filter,
    setFilter,
    refresh,
    fetchLastLead,
  };
};

// Hook to fetch a single lead by ID
export const useLead = (leadId: string | null) => {
  const [lead, setLead] = useState<Lead | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    const fetchLead = async () => {
      if (!leadId) {
        setIsLoading(false);
        return;
      }
      
      setIsLoading(true);
      try {
        const { data, error: queryError } = await supabase
          .from("leads")
          .select("id, name, email, phone, page, created_at")
          .eq("id", leadId)
          .single();
        
        if (queryError) {
          throw queryError;
        }
        
        setLead(data as Lead);
        setError(null);
      } catch (err: any) {
        console.error("Error fetching lead:", err);
        setError(err.message || "Erro ao carregar informações do lead");
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchLead();
  }, [leadId]);
  
  return { lead, isLoading, error };
};
